import { faKitMedical } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { Partner } from '@medprev-web-app/api-graphql-client';

import PartnerStructure from './partner-structure.component';
import { PartnerAddress } from './partner-address.component';

import { ItemList } from '@/app/(main)/(pages)/_components/entities/item-list.component';
import { EntitySection } from '@/app/(main)/(pages)/_components/entities/entity-section.component';
import { EntitySectionDivider } from '@/app/(main)/(pages)/_components/entities/entity-section-divider.component';
import { EntityAbout } from '@/app/(main)/(pages)/_components/entities/entity-about.component';
import { GalleryCarousel } from '@/app/(main)/(pages)/_components/entities/gallery-carousel/gallery-carousel.component';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface PartnerAboutSectionProps {
  partner?: Partner | null;
}

export function PartnerAboutSection({ partner }: PartnerAboutSectionProps) {
  const specialties = (partner?.specialties ?? []).filter((specialty): specialty is string => Boolean(specialty));
  const structure = partner?.facilities[0]?.structure;

  const hasStructure
    = structure?.airConditioning
      || structure?.elevator
      || structure?.parking
      || structure?.specialAccess;

  const hasGalleryImages = !!partner?.facilityImages?.length;

  return (
    <>
      <EntitySection
        data-testid={DataTestIdEnum.PARTNER_PROFILE_SECTION_ADDRESS}
        title="Endereço"
        id="address"
        className="my-0 py-4"
        titleStyles="mb-4 leading-6"
        removeHr
      >
        <PartnerAddress partner={partner} />
      </EntitySection>

      {partner?.description && (
        <>
          <EntitySectionDivider />

          <EntitySection
            title="Sobre o local"
            id="about-place"
            className="my-0 py-4"
            titleStyles="leading-6"
            removeHr
          >
            <EntityAbout
              description={partner.description}
              analyticsEvent={AnalyticsEnum.CLICK_PARTNER_ABOUT_PLACE}
            />
          </EntitySection>
        </>
      )}

      {specialties.length > 0 && (
        <>
          <EntitySectionDivider />

          <EntitySection
            data-testid={DataTestIdEnum.PARTNER_PROFILE_SECTION_SPECIALTIES}
            icon={faKitMedical}
            title="Especialidades atendidas"
            id="specialty"
            className="my-0 py-4"
            titleStyles="leading-6"
            removeHr
          >
            <ItemList
              items={specialties}
              dataTestID="specialties"
              data-analytics-event={AnalyticsEnum.CLICK_PARTNER_SUPPORTED_PROCEDURES}
            />
          </EntitySection>
        </>
      )}

      {(hasStructure || hasGalleryImages) && (
        <>
          <EntitySectionDivider />

          {hasStructure && (
            <EntitySection
              data-testid={DataTestIdEnum.PARTNER_PROFILE_SECTION_STRUCTURE}
              title="Estrutura"
              id="structure"
              className="my-0 py-4"
              titleStyles="mb-4 leading-6"
              removeHr
            >
              <PartnerStructure structure={{
                airConditioning: structure?.airConditioning || false,
                elevator: structure?.elevator || false,
                parking: structure?.parking || false,
                specialAccess: structure?.specialAccess || false,
              }}
              />
            </EntitySection>
          )}

          {hasGalleryImages && (
            <EntitySection className="my-0 py-4" removeHr>
              <GalleryCarousel
                altText={partner?.facilities[0]?.name || ''}
                images={partner?.facilityImages?.map((image) => ({
                  id: image?.id as string,
                  name: image?.name as string,
                  src: image?.src as string,
                })) ?? []}
              />
            </EntitySection>
          )}
        </>
      )}
    </>
  );
}
